import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import styled from 'styled-components';
import { Modal } from 'react-bootstrap';
import moment from 'moment-timezone';

import {
  FullOrderBookingOption,
  Order,
  OrderBookingOption,
} from '../../../types/Order';
import {
  Availabilities,
  BookingOption,
  BookingOptionSubType,
} from '../../../types/Activity';
import * as Service from '../../../services/ActivitiesService';
import Error from '../../stateMessage/Error';
import { Fields } from '../EditOrderForm';
import { Party } from '../../../types/Party';
import OrderBookingOptionsList from '../../order/EditOrderBookingOptionList/OrderBookingOptionsList';
import ActivityBookingOptionsList from '../../order/EditOrderBookingOptionList/ActivityBookingOptionsList';
import { useFetchData } from '../../../infrastructure/hooks/useFetchData';
import { ApiStatus } from '../../../infrastructure/constants/apiStatus';
import { getQuantity } from '../../../infrastructure/utils/order';
import ActivityHiddenWarning from '../components/ActivityHiddenWarning';
import { ActivityContext } from '../../../contexts/Activity';
import { getConfig } from '../../../config';

import AvailabilitiesRows from './components/AvailabilitiesRows';
import { dateFormatter, timeFormatter, eventDateFormatter } from './helpers';

const StyledModal = styled(Modal)`
  &&& {
    .modal-dialog {
      min-width: 800px;
    }
  }
`;

const Title = styled(Modal.Title)`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
`;

type Props = {
  order: Order;
  closeModal(): void;
  party: Party;
  attendeesChanged(id: string, attendeesCount: number): void;
  onSave(fields: Fields): void;
  orderOptions: Array<FullOrderBookingOption>;
  activityOptions: BookingOption[];
  addOrderOptions: (option: Partial<BookingOption>) => void;
  deleteOrderOptions: (option: Partial<BookingOption>) => void;
};

const renderOrderOption = (
  item: OrderBookingOption & { title: string; subType: BookingOptionSubType }
) => {
  const { title, attendees, subType, id } = item;
  return (
    <div style={{ marginBottom: 5 }} key={id}>
      <div style={{ flexDirection: 'column', display: 'flex' }}>
        {title} x {attendees.length}
      </div>
      {subType}
    </div>
  );
};

type OrderOptions = OrderBookingOption & {
  title: string;
  subType: BookingOptionSubType;
};

type AvailabilitiesInfo = {
  availabilities: Availabilities[];
  currentAvailability: Availabilities|null;
  otherAvailabilities: Availabilities[];
}
const INITIAL_AVAILABILITIES_INFO = {availabilities:[], currentAvailability:null, otherAvailabilities:[]};

const getAvailabilitiesInfo = async (activityId:string, bookingOptionId:string, startDate:Date, endDate:Date, tz:string, startDateTime:string|Date|undefined):Promise<AvailabilitiesInfo> => {
  const availabilities = await Service.getBookingOptionAvailabilities({
    activityId,
    bookingOptionId,
    startDate,
    endDate,
    tz
  });

  const currentAvailability = availabilities.find(
    availability =>
      availability.eventDate ===
      eventDateFormatter(startDateTime, tz) ||
      availability.eventDate === startDateTime
  ) ?? {eventDate: eventDateFormatter(startDateTime, tz), capacity: 0 };
  const otherAvailabilities = availabilities.filter(
    availability =>
      availability.eventDate > new Date().toISOString() &&
      availability.eventDate !== currentAvailability.eventDate
  );

  return {availabilities,currentAvailability,otherAvailabilities};
}




const EditPeekAndRedeamOrderForm = ({
  order,
  closeModal,
  attendeesChanged,
  onSave,
  party,
  activityOptions,
  orderOptions,
  addOrderOptions,
  deleteOrderOptions,
}: Props) => {
  const { tz: partyTz = '', startDate, endDate } = party ?? {};

  const {
    name,
    status,
    activity,
    bookingOptions = [],
    startDateTime,
    endDateTime,
  } = order;
  const { title, id: activityId } = activity;

  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);

  const { duration, quantity, isPerPerson } = useMemo(() => {
    const primaryBookedOption = orderOptions.find(
      opt => opt.subType !== BookingOptionSubType.addon
    );
    if (!primaryBookedOption)
      return { duration: 60, quantity: 0, isPerPerson: false };

    setSelectedOptionId(primaryBookedOption.id);

    return {
      duration: primaryBookedOption.duration || 60,
      quantity: getQuantity(
        primaryBookedOption.attendees.length,
        primaryBookedOption.pricingModel,
        primaryBookedOption.subType
      ),
      isPerPerson: primaryBookedOption.pricingModel === 'person',
    };
  }, [orderOptions]);

  const [
    apiStatus,
    info = INITIAL_AVAILABILITIES_INFO,
    fetchAvailabilities,
  ] = useFetchData<AvailabilitiesInfo>();

  const [
    selectedAvailability,
    setSelectedAvailability,
  ] = useState<Availabilities | null>(null);

  const displayOrderOptionsInfo = () => {
    let orderOptions: OrderOptions[] = [];
    for (const { id, title, subType } of activityOptions) {
      for (const item of bookingOptions) {
        if (id === item.id) {
          orderOptions = [
            ...orderOptions,
            { ...item, title, subType },
          ] as OrderOptions[];
        }
      }
    }
    return (
      <div style={{ flexDirection: 'column', display: 'flex' }}>
        {orderOptions.map(renderOrderOption)}
      </div>
    );
  };

  const onOrderUpdate = useCallback(() => {
    if (!selectedAvailability) {
      return;
    }
    const { eventDate } = selectedAvailability;
    const m = moment(eventDate).tz(partyTz);
    const _endDateTime = moment(m).add(duration, 'minutes');
    onSave({
      startDateTime: m.format(),
      endDateTime: _endDateTime.format(),
      timeslot: eventDate,
    });
  }, [onSave, partyTz, selectedAvailability, duration]);

  useEffect(() => {
    if (!startDate || !endDate) return;
    fetchAvailabilities(
      async (): Promise<AvailabilitiesInfo> => {
        if (!selectedOptionId) return INITIAL_AVAILABILITIES_INFO;

        const info = await getAvailabilitiesInfo(
          activityId,
          selectedOptionId,
          startDate,
          endDate,
          partyTz,
          startDateTime
        );

        setSelectedAvailability(info.currentAvailability);

        return info;
      }
    );
  }, [selectedOptionId]);

  const { hidden: hiddenActivity } = useContext(ActivityContext);

  const config = getConfig();

  return (
    <>
      {apiStatus === ApiStatus.error && (
        <Error text='"Unable to get availabilities' />
      )}
      <StyledModal show onHide={closeModal}>
        <Modal.Header closeButton>
          <Title>
            <div>Edit Order {name}</div>
            <div>Order Status: {status}</div>
          </Title>
        </Modal.Header>
        <Modal.Body>
          {hiddenActivity && <ActivityHiddenWarning />}
          <Row>
            <h4>Existing Order Details</h4>
            <a
              className='app-link'
              href={`${config.batchAdminURL}/experiences/${activityId}`}
            >
              {title}
            </a>
            {displayOrderOptionsInfo()}
            <p>Date: {dateFormatter(startDateTime, partyTz)}</p>
            <p>Start Time: {timeFormatter(startDateTime, partyTz)}</p>
            <p>End Time: {timeFormatter(endDateTime, partyTz)}</p>
          </Row>
          <Row>
            {!!orderOptions.length && (
              <>
                <h4>Order Booking Options</h4>
                <OrderBookingOptionsList
                  options={orderOptions}
                  removeBookingOption={deleteOrderOptions}
                  onAttendeesNumberChanged={attendeesChanged}
                />
              </>
            )}
            <hr />
            {!!activityOptions.length && (
              <>
                <h4>Activity Booking Options</h4>
                <ActivityBookingOptionsList
                  options={activityOptions}
                  addBookingOption={addOrderOptions}
                />
              </>
            )}
            <hr />
          </Row>
          <Row>
            <AvailabilitiesRows
              tz={partyTz}
              isPerPerson={isPerPerson}
              isLoading={apiStatus === ApiStatus.loading}
              numOfAttendees={quantity}
              availabilities={info.otherAvailabilities}
              existingBookingOption={info.currentAvailability}
              selectedAvailability={selectedAvailability}
              setSelectedAvailability={setSelectedAvailability}
            />
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <button className='btn btn-def' onClick={closeModal}>
            Cancel
          </button>
          <button className='btn btn-primary' onClick={onOrderUpdate}>
            Save
          </button>
        </Modal.Footer>
      </StyledModal>
    </>
  );
};

export default EditPeekAndRedeamOrderForm;
